import { ApartmentEntry, ApartmentStatus, TListing, TProject } from '../types/config';
import { CLIENTS_WITH_CONTACTS_CONSENT } from './constants';
import { gallerySizes, mapYzerIdToAwsProject } from './server-config';

export const addImageUrlsAvram = async (apt: ApartmentEntry) => {
  const baseUrl = 'https://static.treedis.com/assets';
  const galleryBaseUrl = `${baseUrl}/gallery`;
  const clone: ApartmentEntry = { ...apt };
  const aptKey = apt?.id
    .split('_select')?.[0]
    ?.toLowerCase()
    ?.replace(/\d+$/, '') as keyof typeof gallerySizes;

  clone.floorPlanUrl = `${baseUrl}/${apt?.building}_${apt?.type}_floorplan.webp`;

  if (aptKey) {
    const gallerySize = gallerySizes[aptKey];
    const videoUrl = `${galleryBaseUrl}/${aptKey}-video.mp4`;

    if (gallerySize) {
      clone.galleryUrls = Array.from(
        { length: gallerySize },
        (_, i) => `${galleryBaseUrl}/${aptKey}-${i + 1}.webp`,
      );
      clone.galleryUrls.unshift(videoUrl);
    }

    clone.mainImage = `${galleryBaseUrl}/${aptKey}-hover.webp`;
  } else {
    console.log('Warning: No gallery size found for apt key', aptKey);
    clone.galleryUrls = [`${baseUrl}/apartment_placeholder.webp`];
    clone.mainImage = `${baseUrl}/apartment_placeholder.webp`;
  }

  return clone;
};

export const addImages = async (apt: ApartmentEntry, yzerId: number) => {
  const baseUrl = 'https://static.treedis.com/projects';

  const clone: ApartmentEntry = { ...apt };
  const project = mapYzerIdToAwsProject[yzerId as keyof typeof mapYzerIdToAwsProject];

  if (apt.floorPlanImage) {
    clone.floorPlanUrl = `${baseUrl}/${project}/floorplans/${apt.floorPlanImage}.webp`;
  }

  const galleryUrls = [];

  if (apt.galleryType && apt.galleryCount) {
    for (let i = 1; i <= apt.galleryCount; i++) {
      galleryUrls.push(`${baseUrl}/${project}/gallery/${apt.galleryType}/${i}.webp`);
    }

    if (apt.hasVideo) {
      galleryUrls.unshift(`${baseUrl}/${project}/gallery/${apt.galleryType}/video.mp4`);
    }

    clone.galleryUrls = galleryUrls;
  }

  if (apt.galleryType) {
    clone.mainImage = `${baseUrl}/${project}/gallery/${apt.galleryType}/main_image.webp`;
  } else if (apt.floorPlanImage) {
    // If there's no gallery type but we have a floor plan image, use it as the main image
    clone.mainImage = `${baseUrl}/${project}/floorplans/${apt.floorPlanImage}.webp`;
  }

  return clone;
};

export const processProject = (p: TProject, awsUrl: string): TProject => {
  const clone = { ...p };

  clone.logoUrl = p.logoUrl ? `${awsUrl}${p.logoUrl}` : '';
  clone.mainImageUrl = p.mainImageUrl ? `${awsUrl}${p.mainImageUrl}` : '';
  clone.hoverImageUrl = p.hoverImageUrl ? `${awsUrl}${p.hoverImageUrl}` : '';
  clone.withConsent = false;

  if (p.assets) {
    clone.assets = p.assets.map((a) => ({
      ...a,
      url: a.url ? `${awsUrl}${a.url}` : '',
      urlLarge: a.urlLarge ? `${awsUrl}${a.urlLarge}` : '',
      urlSmall: a.urlSmall ? `${awsUrl}${a.urlSmall}` : '',
      urlMedium: a.urlMedium ? `${awsUrl}${a.urlMedium}` : '',
    }));
  }

  if (p.clients) {
    clone.clients = p.clients.map((c) => {
      if (CLIENTS_WITH_CONTACTS_CONSENT.includes(c.id)) {
        clone.withConsent = true;
      }
      return {
        ...c,
        logoUrl: c.logoUrl ? `${awsUrl}${c.logoUrl}` : '',
      };
    });
  }

  if (p.specifications && typeof p.specifications === 'string') {
    const parsedSpecs = JSON.parse(p.specifications);
    clone.specifications = Array.isArray(parsedSpecs) ? parsedSpecs : [];
  }

  return clone;
};

/**
 * Process project data with selective database/draft merging based on notInDraftProjectProps configuration
 * @param databaseProject - Project data from database
 * @param draftProject - Project data from draft (S3)
 * @param awsUrl - AWS URL for asset processing
 * @param noDraftProps - Object specifying which TProject properties should use database data
 * @returns Processed project with mixed data sources
 */
export const processProjectWithNotInDraftProps = (
  databaseProject: TProject,
  draftProject: TProject,
  awsUrl: string,
  noDraftProps: Record<string, boolean> = {},
): TProject => {
  // Start with draft data as base
  let mergedProject = { ...draftProject };

  // Override with database data for specified properties
  Object.keys(noDraftProps).forEach((propKey) => {
    if (noDraftProps[propKey] && databaseProject && propKey in databaseProject) {
      const dbValue = (databaseProject as any)[propKey];

      // Only override if database has a valid value
      if (dbValue === null || dbValue === undefined) {
        return;
      }

      // Special handling for different data types - use early returns
      if (typeof dbValue === 'string' && dbValue.trim() !== '') {
        (mergedProject as any)[propKey] = dbValue;
        return;
      }

      if (typeof dbValue === 'number' && !isNaN(dbValue)) {
        (mergedProject as any)[propKey] = dbValue;
        return;
      }

      if (Array.isArray(dbValue) && dbValue.length > 0) {
        (mergedProject as any)[propKey] = dbValue;
        return;
      }

      if (typeof dbValue === 'object' && dbValue !== null) {
        (mergedProject as any)[propKey] = dbValue;
        return;
      }

      if (typeof dbValue === 'boolean') {
        (mergedProject as any)[propKey] = dbValue;
      }
    }
  });

  // Process the merged project with AWS URLs
  return processProject(mergedProject, awsUrl);
};

const EARTH_RADIUS_KM = 6371;
const toRadians = (degrees: number) => degrees * (Math.PI / 180);
const toDegrees = (radians: number) => radians * (180 / Math.PI);

// Get the top-right and bottom-left coordinates of a square around a point
export const getInAreaSquareBounds = (
  lat?: number,
  lng?: number,
  distanceKm?: number,
  asString: boolean = false,
) => {
  if (!lat || !lng || !distanceKm) return { topRightCord: 0, bottomLeftCord: 0 };

  const halfDistanceKm = distanceKm / 2;

  // Angular distance in radians on Earth's surface
  const angularDistance = halfDistanceKm / EARTH_RADIUS_KM;

  // Latitude in radians
  const latRad = toRadians(lat);

  // Calculate the latitude bounds
  const minLat = latRad - angularDistance;
  const maxLat = latRad + angularDistance;

  const deltaLng = Math.asin(Math.sin(angularDistance) / Math.cos(latRad));

  const minLng = toRadians(lng) - deltaLng;
  const maxLng = toRadians(lng) + deltaLng;

  // Convert bounds back to degrees
  const topRightCord = { lat: toDegrees(maxLat), lng: toDegrees(maxLng) };

  const bottomLeftCord = { lat: toDegrees(minLat), lng: toDegrees(minLng) };

  return {
    topRightCord: asString ? `${topRightCord.lat},${topRightCord.lng}` : topRightCord,
    bottomLeftCord: asString ? `${bottomLeftCord.lat},${bottomLeftCord.lng}` : bottomLeftCord,
  };
};

/**
 * Transforms a database Listing record to ApartmentEntry format
 * for backward compatibility with the existing showcase system
 */
export const transformListingToApartmentEntry = (listing: TListing): ApartmentEntry => {
  // Generate a unique ID similar to hardcoded format
  const polygonId =
    listing.showcasePolygonId ||
    `${listing.building || 'B'}_${listing.type || listing.name}_select_floor${
      listing.minFloor || 0
    }`;

  // Use nrId if available, otherwise fall back to polygon.id or polygon.number
  const nrId = listing.number || listing.id;

  // Determine floor - use minFloor as the primary floor value
  const floor = listing.minFloor || 0;

  // Map polygon status to ApartmentStatus enum
  const status = listing.status === 'available' ? ApartmentStatus.Available : ApartmentStatus.Sold;

  return {
    id: polygonId,
    nrId: nrId,
    building: listing.building || `Building${listing.buildingNumber || '1'}`,
    number: listing.number || listing.id,
    type: listing.type || listing.name || 'apartment',
    area: listing.livingArea || 0,
    status: status,
    rooms: listing.rooms || 0,
    floor: floor,
    balconyArea: listing.balconyArea || 0,
    // Optional properties with placeholder values for now
    liked: false,
    price: listing.price || undefined,
    tourUrl: listing.tourUrl || undefined,
    elevator: listing.elevator || undefined,
    shelter: listing.shelter || undefined,
    kind: listing.kind || undefined,
    // Image-related properties - placeholders for future implementation
    hasVideo: false,
    galleryType: undefined,
    galleryCount: undefined,
    galleryUrls: undefined,
    videoUrl: undefined,
    mainImage: undefined,
    floorPlanUrl: undefined,
    floorPlanImage: undefined,
    specialInfo: undefined,
  };
};
